// Share a file to any app/messenger via the Web Share API (navigator.share with
// files). Falls back to a normal download when file-sharing isn't supported
// (e.g. Telegram Desktop / older WebViews) or when the OS share sheet errors.
import { saveBlob } from "./download";

export const MIME = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
} as const;

interface NavShare {
  share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
  canShare?: (data: { files?: File[] }) => boolean;
}

/** Whether the current environment can share files (not just links/text). */
export function canShareFiles(): boolean {
  try {
    const nav = navigator as unknown as NavShare;
    if (!nav.share || !nav.canShare) return false;
    const probe = new File(["x"], "probe.txt", { type: "text/plain" });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

export type ShareResult = "shared" | "downloaded";

/**
 * Try to share the blob as a file via the OS share sheet; otherwise download it.
 * A user cancelling the share sheet counts as handled (no download fallback).
 */
export async function shareOrSave(
  blob: Blob,
  filename: string,
  mime: string,
  title: string,
): Promise<ShareResult> {
  const type = mime || blob.type || "application/octet-stream";
  const file = new File([blob], filename, { type });
  const nav = navigator as unknown as NavShare;
  if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title });
      return "shared";
    } catch (e) {
      // User dismissed the sheet → treat as done, don't also download.
      const name = (e as { name?: string })?.name;
      if (name === "AbortError") return "shared";
      // Any other failure → fall through to download.
    }
  }
  saveBlob(blob, filename);
  return "downloaded";
}
